import React, { useEffect, useState } from 'react';
import { X, BarChart3, PieChart, TrendingUp, AreaChart } from 'lucide-react';
import ChartDisplay from './ChartDisplay';
import { ChartType, DataPoint } from './types';

interface ChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DataPoint[];
  initialChartType: ChartType;
  valueKeys: string[];
  labelKey: string;
}

const chartOptions: { type: ChartType; icon: React.ReactNode; label: string }[] = [
  { type: 'bar', icon: <BarChart3 className="w-4 h-4" />, label: 'Barras' },
  { type: 'pie', icon: <PieChart className="w-4 h-4" />, label: 'Pizza' },
  { type: 'line', icon: <TrendingUp className="w-4 h-4" />, label: 'Linha' },
  { type: 'area', icon: <AreaChart className="w-4 h-4" />, label: 'Área' },
];

const ChartModal: React.FC<ChartModalProps> = ({
  isOpen,
  onClose,
  data,
  initialChartType,
  valueKeys,
  labelKey,
}) => {
  const [chartType, setChartType] = useState<ChartType>(initialChartType);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setChartType(initialChartType);
  }, [initialChartType]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Gráfico expandido"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        className={`relative bg-white rounded-lg shadow-2xl w-[95vw] max-w-6xl max-h-[90vh] flex flex-col transition-all duration-300 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Visualização do gráfico</h2>
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              {chartOptions.map(opt => (
                <button
                  key={opt.type}
                  onClick={() => setChartType(opt.type)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    chartType === opt.type
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title={opt.label}
                >
                  {opt.icon}
                  <span className="hidden sm:inline">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="min-h-[500px]">
            <ChartDisplay
              data={data}
              chartType={chartType}
              valueKeys={valueKeys}
              labelKey={labelKey}
              height={500}
            />
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <span className="text-xs text-gray-500">
            {data.length} registro{data.length !== 1 ? 's' : ''} | {valueKeys.length} métrica{valueKeys.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ChartModal;
